import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import Swal from "sweetalert2";
import {
    AlertCircle,
    CalendarDays,
    Check,
    Clock,
    Download,
    Edit3,
    FileSpreadsheet,
    History,
    RefreshCw,
    Save,
    Search,
    Trash2,
    Upload,
    UserRound,
    WalletCards,
} from "lucide-react";

import { API_BASE_URL, extractErrorMessage } from "../../../../../api";
import { getAllUsersRequest } from "../../../../redux/actions/userActions";
import "../../Mesrequetes/MesrequetesStyles/DemandeConges.css";
import "./GestionSalaires.css";

const emptyDraft = {
    augmentationPct: "",
    primePct: "",
    devise: "MAD",
    dateEffet: new Date().toISOString().slice(0, 10),
    dateFin: "",
    motif: "",
};

const formatMoney = (value, devise = "MAD") => {
    const number = Number(value);
    if (!Number.isFinite(number)) return "-";
    return `${number.toLocaleString("fr-FR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })} ${devise || "MAD"}`;
};

const formatDate = (value) => {
    if (!value) return "-";
    return new Date(value).toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

const getUserName = (user) =>
    user?.fullName ||
    `${user?.firstName || ""} ${user?.lastName || ""}`.trim() ||
    user?.username ||
    "Collaborateur";

const getInitial = (name) => (name || "C").trim().charAt(0).toUpperCase();

const toAmount = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : 0;
};

const toPct = (value) => {
    const number = Number(value);
    return Number.isFinite(number) && number >= 0 ? number : 0;
};

const GestionSalaires = () => {
    const dispatch = useDispatch();
    const fileInputRef = useRef(null);
    const users = useSelector((state) => state.user?.users || []);
    const usersLoading = useSelector((state) => state.user?.loading || false);

    const [activeTab, setActiveTab] = useState("gestion");
    const [searchTerm, setSearchTerm] = useState("");
    const [salaires, setSalaires] = useState([]);
    const [loading, setLoading] = useState(false);
    const [drafts, setDrafts] = useState({});
    const [editingId, setEditingId] = useState(null);
    const [editingUserId, setEditingUserId] = useState(null);
    const [bulkImporting, setBulkImporting] = useState(false);

    const token = sessionStorage.getItem("token");
    const uid = sessionStorage.getItem("uid");

    const headers = useMemo(() => ({
        Authorization: token?.startsWith("Bearer ") ? token : `Bearer ${token}`,
        uid,
        "Content-Type": "application/json",
    }), [token, uid]);

    const loadSalaires = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/api/salaires`, { headers });
            setSalaires(response.data || []);
        } catch (error) {
            const message = extractErrorMessage(error, "Erreur lors du chargement des salaires.");
            Swal.fire({ icon: "error", title: "Erreur", text: message });
        } finally {
            setLoading(false);
        }
    }, [headers]);

    useEffect(() => {
        dispatch(getAllUsersRequest());
        loadSalaires();
    }, [dispatch, loadSalaires]);

    const currentSalaryByUser = useMemo(() => {
        const map = new Map();
        [...salaires]
            .filter((s) => !s.dateFin)
            .sort((a, b) => new Date(b.dateEffet || 0) - new Date(a.dateEffet || 0))
            .forEach((s) => {
                if (!map.has(String(s.userId))) map.set(String(s.userId), s);
            });
        return map;
    }, [salaires]);

    const rows = useMemo(() => {
        const salaryUsers = salaires
            .filter((s) => !users.some((u) => String(u.id) === String(s.userId)))
            .map((s) => ({
                id: s.userId,
                fullName: s.userFullName,
                employeeCode: s.employeeCode,
                jobName: s.jobName,
                entityName: s.entityName,
            }));

        const allUsers = [...users, ...salaryUsers];
        const normalizedSearch = searchTerm.trim().toLowerCase();

        return allUsers
            .filter((u) => {
                if (!normalizedSearch) return true;
                return [
                    getUserName(u),
                    u.employeeCode,
                    u.jobName,
                    u.entityName,
                ].some((value) => String(value || "").toLowerCase().includes(normalizedSearch));
            })
            .sort((a, b) => getUserName(a).localeCompare(getUserName(b), "fr"));
    }, [salaires, searchTerm, users]);

    const filteredHistory = useMemo(() => {
        const normalizedSearch = searchTerm.trim().toLowerCase();
        return salaires.filter((s) => {
            if (!normalizedSearch) return true;
            return [
                s.userFullName,
                s.employeeCode,
                s.motif,
                s.devise,
            ].some((value) => String(value || "").toLowerCase().includes(normalizedSearch));
        });
    }, [salaires, searchTerm]);

    const getDraft = (userId) => drafts[userId] || emptyDraft;

    const updateDraft = (userId, field, value) => {
        setDrafts((prev) => ({
            ...prev,
            [userId]: {
                ...emptyDraft,
                ...(prev[userId] || {}),
                [field]: value,
            },
        }));
    };

    // Calcule le salaire projeté à partir des pourcentages saisis
    const getProjectedNet = (draft, current) => {
        const currentNet = toAmount(current?.salaireNet);
        const augAmount = currentNet * toPct(draft.augmentationPct) / 100;
        const primeAmount = currentNet * toPct(draft.primePct) / 100;
        return currentNet + augAmount + primeAmount;
    };

    // Montant brut de l'augmentation (pour envoi au backend)
    const getAugmentationAmount = (draft, current) => {
        const currentNet = toAmount(current?.salaireNet);
        return currentNet * toPct(draft.augmentationPct) / 100;
    };

    // Montant brut de la prime (pour envoi au backend)
    const getPrimeAmount = (draft, current) => {
        const currentNet = toAmount(current?.salaireNet);
        return currentNet * toPct(draft.primePct) / 100;
    };

    const startEdit = (salary) => {
        const current = currentSalaryByUser.get(String(salary.userId));
        const currentNet = toAmount(current?.salaireNet);
        const targetNet = toAmount(salary.salaireNet);

        // Reconstitue le % d'augmentation à partir des montants stockés
        const diffNet = targetNet - currentNet;
        const augPct = currentNet > 0 ? (diffNet / currentNet) * 100 : 0;

        setActiveTab("gestion");
        setEditingId(salary.id);
        setEditingUserId(salary.userId);
        setDrafts((prev) => ({
            ...prev,
            [salary.userId]: {
                augmentationPct: augPct > 0 ? String(augPct.toFixed(2)) : "",
                primePct: "",
                devise: salary.devise || "MAD",
                dateEffet: salary.dateEffet || emptyDraft.dateEffet,
                dateFin: salary.dateFin || "",
                motif: salary.motif || "",
            },
        }));
    };

    const resetDraft = (userId) => {
        setDrafts((prev) => {
            const next = { ...prev };
            delete next[userId];
            return next;
        });
        setEditingId(null);
        setEditingUserId(null);
    };

    const validateDraft = (draft, current) => {
        if (getProjectedNet(draft, current) <= 0) return "Le salaire net final doit être supérieur à 0.";
        if (!draft.dateEffet) return "La date d'effet est obligatoire.";
        if (draft.dateFin && draft.dateFin < draft.dateEffet) {
            return "La date de fin doit être supérieure ou égale à la date d'effet.";
        }
        return "";
    };

    const saveSalary = async (userId) => {
        const draft = getDraft(userId);
        const current = currentSalaryByUser.get(String(userId));
        const projectedNet = getProjectedNet(draft, current);
        const validationError = validateDraft(draft, current);
        if (validationError) {
            Swal.fire({ icon: "warning", title: "Données incomplètes", text: validationError });
            return;
        }

        const payload = {
            userId: Number(userId),
            salaireBrut: current?.salaireBrut ?? null,
            salaireNet: projectedNet,
            devise: draft.devise || current?.devise || "MAD",
            dateEffet: draft.dateEffet,
            dateFin: draft.dateFin || null,
            motif: draft.motif || null,
        };

        setLoading(true);
        const wasEditingThisUser = editingId && String(editingUserId) === String(userId);
        try {
            if (wasEditingThisUser) {
                await axios.put(`${API_BASE_URL}/api/salaires/${editingId}`, payload, { headers });
            } else {
                await axios.post(`${API_BASE_URL}/api/salaires`, payload, { headers });
            }
            await loadSalaires();
            resetDraft(userId);
            Swal.fire({
                icon: "success",
                title: wasEditingThisUser ? "Salaire modifié" : "Salaire enregistré",
                timer: 1600,
                showConfirmButton: false,
            });
        } catch (error) {
            const message = extractErrorMessage(error, "Erreur lors de l'enregistrement du salaire.");
            Swal.fire({ icon: "error", title: "Erreur", text: message });
        } finally {
            setLoading(false);
        }
    };

    const deleteSalary = async (salary) => {
        const confirm = await Swal.fire({
            icon: "warning",
            title: "Supprimer cette ligne ?",
            text: "Cette action retirera cette ligne de l'historique des salaires.",
            showCancelButton: true,
            confirmButtonText: "Supprimer",
            cancelButtonText: "Annuler",
        });
        if (!confirm.isConfirmed) return;

        setLoading(true);
        try {
            await axios.delete(`${API_BASE_URL}/api/salaires/${salary.id}`, { headers });
            await loadSalaires();
            Swal.fire({ icon: "success", title: "Salaire supprimé", timer: 1400, showConfirmButton: false });
        } catch (error) {
            const message = extractErrorMessage(error, "Erreur lors de la suppression.");
            Swal.fire({ icon: "error", title: "Erreur", text: message });
        } finally {
            setLoading(false);
        }
    };

    const downloadSalaryTemplate = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/salaires/template-excel`, {
                responseType: "blob",
                headers: {
                    Authorization: headers.Authorization,
                    uid,
                },
            });
            const blob = new Blob([response.data], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = "modele_salaires.xlsx";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(link.href);
        } catch (error) {
            const message = extractErrorMessage(error, "Erreur lors du telechargement du modele Excel.");
            Swal.fire({ icon: "error", title: "Erreur", text: message });
        }
    };

    const handleExcelImport = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) return;

        const confirm = await Swal.fire({
            icon: "question",
            title: "Importer les salaires ?",
            text: "Le fichier Excel doit contenir les colonnes Nom, Matricule et Salaire_net.",
            showCancelButton: true,
            confirmButtonText: "Importer",
            cancelButtonText: "Annuler",
        });
        if (!confirm.isConfirmed) return;

        const formData = new FormData();
        formData.append("file", file);

        setBulkImporting(true);
        try {
            const response = await axios.post(`${API_BASE_URL}/api/salaires/import-excel`, formData, {
                headers: {
                    Authorization: headers.Authorization,
                    uid,
                    "Content-Type": "multipart/form-data",
                },
            });

            const result = response.data || {};
            await loadSalaires();

            const errors = result.errors || [];
            let html = `<b>${result.imported || 0}</b> salaire(s) enregistre(s)<br/><b>${result.skipped || 0}</b> ligne(s) ignoree(s)`;
            if (errors.length > 0) {
                html += `<br/><br/><details><summary>Voir les erreurs</summary><ul style="text-align:left;font-size:12px;max-height:220px;overflow:auto">`;
                errors.forEach((error) => { html += `<li>${error}</li>`; });
                html += `</ul></details>`;
            }

            setActiveTab("historique");
            Swal.fire({
                icon: result.skipped > 0 ? "warning" : "success",
                title: "Import termine",
                html,
            });
        } catch (error) {
            const message = extractErrorMessage(error, "Erreur lors de l'import Excel des salaires.");
            Swal.fire({ icon: "error", title: "Erreur", text: message });
        } finally {
            setBulkImporting(false);
        }
    };

    const renderEmployee = (user, salary) => {
        const name = getUserName(user) || salary?.userFullName;
        return (
            <div className="gsmEmpCell">
                <div className="gsmAvatar">{getInitial(name)}</div>
                <div>
                    <div className="gsmEmpName">{name}</div>
                    <div className="dcTiny">{user?.employeeCode || salary?.employeeCode || "-"}</div>
                </div>
            </div>
        );
    };

    const isBusy = loading || usersLoading;

    return (
        <div className="dcPage scmPage gsmPage">
            <div className="dcWrap dcMax">
                <div className="dcHeader gsmHeader">
                    <div>
                        <h1>Gestion des salaires</h1>
                    </div>
                    <button className="dcBtn dcBtn--outline gsmRefreshBtn" type="button" onClick={loadSalaires}>
                        <RefreshCw size={16} />
                        Actualiser
                    </button>
                </div>

                <div className="dcCard">
                    <div className="dcTabs">
                        <button
                            className={`dcTab ${activeTab === "gestion" ? "is-active" : ""}`}
                            type="button"
                            onClick={() => setActiveTab("gestion")}
                        >
                            <WalletCards size={16} /> Gestion
                        </button>
                        <button
                            className={`dcTab ${activeTab === "import" ? "is-active" : ""}`}
                            type="button"
                            onClick={() => setActiveTab("import")}
                        >
                            <Upload size={16} /> Import massif
                        </button>
                        <button
                            className={`dcTab ${activeTab === "historique" ? "is-active" : ""}`}
                            type="button"
                            onClick={() => setActiveTab("historique")}
                        >
                            <History size={16} /> Historique des salaires
                        </button>
                    </div>

                    <div className="dcBody">
                        <div className="gsmToolbar">
                            <div className="gsmSearch">
                                <Search size={16} />
                                <input
                                    type="text"
                                    placeholder="Rechercher un collaborateur, matricule, poste ou motif..."
                                    value={searchTerm}
                                    onChange={(event) => setSearchTerm(event.target.value)}
                                    className="dcInput"
                                />
                            </div>
                            <div className="gsmCounter">
                                <UserRound size={15} />
                                {activeTab === "gestion" ? rows.length : activeTab === "import" ? salaires.length : filteredHistory.length} ligne(s)
                            </div>
                        </div>

                        {activeTab === "gestion" && (
                            <div className="dcTableWrap gsmTableWrap">
                                <table className="dcTable gsmTable">
                                    <thead>
                                    <tr>
                                        <th>Collaborateur</th>
                                        <th>Poste / entité</th>
                                        <th>Salaire net actuel</th>
                                        <th>Aug. %</th>
                                        <th>Prime %</th>
                                        <th>Salaire deviendra</th>
                                        {/*<th>Total aug. %</th>*/}
                                        <th>Motif</th>
                                        <th>Action</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {isBusy ? (
                                        <tr>
                                            <td colSpan="10">
                                                <div className="dcEmpty"><Clock size={24} /><div>Chargement...</div></div>
                                            </td>
                                        </tr>
                                    ) : rows.length === 0 ? (
                                        <tr>
                                            <td colSpan="10">
                                                <div className="dcEmpty"><AlertCircle size={24} /><div>Aucun collaborateur trouvé</div></div>
                                            </td>
                                        </tr>
                                    ) : (
                                        rows.map((user) => {
                                            const current = currentSalaryByUser.get(String(user.id));
                                            const draft = getDraft(user.id);
                                            const currentNet = toAmount(current?.salaireNet);
                                            const augPct = toPct(draft.augmentationPct);
                                            const primePct = toPct(draft.primePct);
                                            const totalPct = augPct + primePct;
                                            const projectedNet = getProjectedNet(draft, current);
                                            const devise = draft.devise || current?.devise || "MAD";
                                            const isEditingThisUser = editingId && String(editingUserId) === String(user.id);

                                            return (
                                                <tr key={user.id}>
                                                    <td>{renderEmployee(user, current)}</td>
                                                    <td>
                                                        <div className="gsmRole">{user.jobName || current?.jobName || "-"}</div>
                                                        <div className="dcTiny">{user.entityName || current?.entityName || "-"}</div>
                                                    </td>
                                                    <td className="gsmMoney gsmStrong">{formatMoney(current?.salaireNet, current?.devise)}</td>
                                                    <td>
                                                        <div className="gsmPctInput">
                                                            <input
                                                                className="gsmMiniInput"
                                                                type="number"
                                                                min="0"
                                                                step="0.01"
                                                                value={draft.augmentationPct}
                                                                onChange={(event) => updateDraft(user.id, "augmentationPct", event.target.value)}
                                                                placeholder="0.00"
                                                            />
                                                            <span className="gsmPctSymbol">%</span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="gsmMoneyInput">
                                                            <div className="gsmPctInput">
                                                                <input
                                                                    className="gsmMiniInput"
                                                                    type="number"
                                                                    min="0"
                                                                    step="0.01"
                                                                    value={draft.primePct}
                                                                    onChange={(event) => updateDraft(user.id, "primePct", event.target.value)}
                                                                    placeholder="0.00"
                                                                />
                                                                <span className="gsmPctSymbol">%</span>
                                                            </div>
                                                            <select
                                                                className="gsmCurrency"
                                                                value={devise}
                                                                onChange={(event) => updateDraft(user.id, "devise", event.target.value)}
                                                            >
                                                                <option value="MAD">MAD</option>
                                                                <option value="EUR">EUR</option>
                                                                <option value="USD">USD</option>
                                                            </select>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="gsmProjected">
                                                            {formatMoney(projectedNet, devise)}
                                                        </div>
                                                    </td>
                                                    {/*<td>*/}
                                                    {/*    <span className={totalPct >= 0 ? "gsmPercent gsmPercent--up" : "gsmPercent gsmPercent--down"}>*/}
                                                    {/*        {totalPct.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %*/}
                                                    {/*    </span>*/}
                                                    {/*</td>*/}

                                                    <td>
                                                        <input
                                                            className="gsmMotifInput"
                                                            type="text"
                                                            maxLength={100}
                                                            value={draft.motif}
                                                            onChange={(event) => updateDraft(user.id, "motif", event.target.value)}
                                                            placeholder="augmentation, promotion..."
                                                        />
                                                    </td>
                                                    <td>
                                                        <div className="gsmRowActions">
                                                            <button
                                                                className="gsmIconBtn gsmIconBtn--save"
                                                                type="button"
                                                                title={editingId ? "Modifier" : "Enregistrer"}
                                                                onClick={() => saveSalary(user.id)}
                                                                disabled={loading}
                                                            >
                                                                {isEditingThisUser ? <Check size={16} /> : <Save size={16} />}
                                                            </button>
                                                            <button
                                                                className="gsmIconBtn"
                                                                type="button"
                                                                title="Réinitialiser"
                                                                onClick={() => resetDraft(user.id)}
                                                            >
                                                                <RefreshCw size={15} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {activeTab === "import" && (
                            <div className="gsmBulk">
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    accept=".xlsx,.xls"
                                    style={{ display: "none" }}
                                    onChange={handleExcelImport}
                                />
                                <div className="gsmBulkPanel gsmExcelImportPanel">
                                    <div className="gsmBulkHead">
                                        <div>
                                            <h2><FileSpreadsheet size={18} /> Import Excel des salaires</h2>
                                            <p>Le fichier doit contenir trois colonnes : Nom, Matricule et Salaire_net. Le matricule sert a retrouver le collaborateur, puis le salaire net est enregistre dans la table salaires.</p>
                                        </div>
                                        <div className="gsmBulkActions">
                                            <button className="dcBtn dcBtn--outline" type="button" onClick={downloadSalaryTemplate} disabled={bulkImporting}>
                                                <Download size={16} /> Modele Excel
                                            </button>
                                            <button
                                                className="dcBtn dcBtn--primary"
                                                type="button"
                                                onClick={() => fileInputRef.current?.click()}
                                                disabled={bulkImporting}
                                            >
                                                <Upload size={16} />
                                                {bulkImporting ? "Import..." : "Importer Excel"}
                                            </button>
                                        </div>
                                    </div>

                                    <div className="gsmExcelFormat">
                                        <div className="gsmExcelColumn">Nom</div>
                                        <div className="gsmExcelColumn">Matricule</div>
                                        <div className="gsmExcelColumn">Salaire_net</div>
                                    </div>
                                </div>
                            </div>
                        )}
                        {activeTab === "historique" && (
                            <div className="dcTableWrap gsmTableWrap">
                                <table className="dcTable gsmTable gsmHistoryTable">
                                    <thead>
                                    <tr>
                                        <th>Collaborateur</th>
                                        <th>Salaire brut</th>
                                        <th>Salaire net</th>
                                        <th>Devise</th>
                                        <th>Date effet</th>
                                        <th>Date fin</th>
                                        <th>Motif</th>
                                        <th>Créé le</th>
                                        <th>Actions</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {isBusy ? (
                                        <tr>
                                            <td colSpan="9">
                                                <div className="dcEmpty"><Clock size={24} /><div>Chargement...</div></div>
                                            </td>
                                        </tr>
                                    ) : filteredHistory.length === 0 ? (
                                        <tr>
                                            <td colSpan="9">
                                                <div className="dcEmpty"><CalendarDays size={24} /><div>Aucun historique trouvé</div></div>
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredHistory.map((salary) => (
                                            <tr key={salary.id}>
                                                <td>{renderEmployee({ fullName: salary.userFullName, employeeCode: salary.employeeCode }, salary)}</td>
                                                <td className="gsmMoney">{formatMoney(salary.salaireBrut, salary.devise)}</td>
                                                <td className="gsmMoney gsmStrong">{formatMoney(salary.salaireNet, salary.devise)}</td>
                                                <td><span className="gsmCurrencyBadge">{salary.devise || "MAD"}</span></td>
                                                <td>{formatDate(salary.dateEffet)}</td>
                                                <td>{salary.dateFin ? formatDate(salary.dateFin) : <span className="gsmCurrentBadge">Actuel</span>}</td>
                                                <td>{salary.motif || "-"}</td>
                                                <td>{formatDate(salary.createdAt)}</td>
                                                <td>
                                                    <div className="gsmRowActions">
                                                        <button
                                                            className="gsmIconBtn"
                                                            type="button"
                                                            title="Modifier"
                                                            onClick={() => startEdit(salary)}
                                                        >
                                                            <Edit3 size={15} />
                                                        </button>
                                                        <button
                                                            className="gsmIconBtn gsmIconBtn--danger"
                                                            type="button"
                                                            title="Supprimer"
                                                            onClick={() => deleteSalary(salary)}
                                                        >
                                                            <Trash2 size={15} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GestionSalaires;